import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Button from "@components/Button";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import { ROUTES } from "@stores/AccountStore";
import { observer } from "mobx-react-lite";
import { useStores } from "@stores";
import { useLang } from "@src/hooks/useLang";
import AuthLayout, { ErrorAlert } from "@components/AuthLayout";
import { authClient } from "@services/authService";
import Text from "@components/Text";
import { consumeSessionExpiredNotice } from "@src/utils/sessionExpired";
import {
  clearPendingOAuthQuery,
  currentOAuthQuery,
  followOAuthRedirect,
  oauthRedirectHost,
  readOAuthQuery,
  savePendingOAuthQuery,
} from "@src/utils/oauthRequest";

const LoginScreen: React.FC = () => {

  const lang = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const { accountStore } = useStores();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  // Set when the app bounced us here because the session died mid-use, so the
  // user isn't left guessing why their tab suddenly became a login form.
  const [sessionExpired, setSessionExpired] = React.useState(
    consumeSessionExpiredNotice
  );
  // An AI assistant (ChatGPT, Claude) sent the user here to connect it: after
  // sign-in the backend continues to the consent screen instead of /fields.
  const [oauthQuery, setOAuthQuery] = React.useState(() => currentOAuthQuery(location.search));
  const [continuing, setContinuing] = React.useState(false);

  React.useEffect(() => {
    // Survives the Register → Verify email detour, where the query is lost.
    const fromUrl = readOAuthQuery(location.search);
    if (fromUrl) savePendingOAuthQuery(fromUrl);
  }, [location.search]);

  const alert = error ?? (sessionExpired ? lang.auth.sessionExpired : null);
  const oauthApp = oauthQuery ? oauthRedirectHost(oauthQuery) ?? lang.aiConnector.unknownApp : null;

  // While connecting an assistant the form stays even for a signed-in user:
  // the backend only sends a session it cannot see (or prompt=login) here.
  if (accountStore.authenticated && !oauthQuery && !continuing) {
    return <Navigate to={ROUTES.FIELDS} />;
  }

  const signInForAssistant = async (query: string) => {
    const { data, error } = await authClient.$fetch<{ redirect?: boolean; url?: string }>(
      "/sign-in/email",
      { method: "POST", body: { email, password, oauth_query: query } }
    );
    if (error) {
      // $fetch's error carries the response body's fields untyped.
      const body = error as { code?: string; error?: string };
      if (error.status === 403 && body.code === "EMAIL_NOT_VERIFIED") {
        navigate(ROUTES.VERIFY_EMAIL, { state: { email } });
        return;
      }
      // The signed request is only valid for 10 minutes.
      if (body.error === "invalid_signature") {
        clearPendingOAuthQuery();
        setOAuthQuery(null);
        setError(lang.aiConnector.requestExpired);
      } else {
        setError(error.message || "Authentication failed");
      }
      setLoading(false);
      return;
    }
    clearPendingOAuthQuery();
    setContinuing(true);
    if (followOAuthRedirect(data)) return;
    // No redirect came back (should not happen): just land in the app.
    await accountStore.auth();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSessionExpired(false);
    setLoading(true);

    try {
      if (oauthQuery) {
        await signInForAssistant(oauthQuery);
        return;
      }
      const { error } = await authClient.signIn.email({ email, password });
      if (error) {
        // Unverified account: the backend already re-sent a code (sendOnSignIn),
        // so go straight to the code-entry screen.
        if (error.status === 403 && error.code === "EMAIL_NOT_VERIFIED") {
          navigate(ROUTES.VERIFY_EMAIL, { state: { email } });
          return;
        }
        setError(error.message || "Authentication failed");
        setLoading(false);
        return;
      }
      await accountStore.auth();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed");
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit} style={{ width: 336 }}>
        <ErrorAlert enabled={!!alert}>{alert}</ErrorAlert>
        {oauthApp && (
          <>
            <Text size={14} color="#899093" style={{ textAlign: "center", width: "100%" }}>
              {continuing
                ? lang.aiConnector.loginContinuing
                : lang.aiConnector.loginHint.replace("{app}", oauthApp)}
            </Text>
            <SizedBox height={16} />
          </>
        )}
        <Input
          label={lang.auth.emailTitle}
          placeholder={lang.auth.emailField}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <SizedBox height={16} />
        <Input
          label={lang.auth.passwordTitle}
          placeholder={lang.auth.passwordField}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          extra={lang.auth.passwordRestore}
          onExtraClick={() => navigate(ROUTES.PASSWORD_FORGOT)}
          required
        />
        <SizedBox height={24} />
        <Button type="submit" disabled={loading || continuing}>
          {lang.auth.signInButton}
        </Button>
        <SizedBox height={16} />
        <Text
          size={14}
          color="#68AD18"
          style={{ cursor: "pointer", textAlign: "center", width: "100%" }}
          onClick={() => navigate(ROUTES.REGISTER)}
        >
          {lang.auth.signUpButton}
        </Text>
      </form>
    </AuthLayout>
  );
};

export default observer(LoginScreen);
